import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEO from '../../components/feature/SEO';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const REAL_PHOTOS = [
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/a865868d-d9df-4c45-8453-95cdd0b15b6d_Quantum-Electrical-Services-Sleaford---Tesla-EV-Charger-Installation.jpg?v=916cc30bfa4bcbb570ebda20fce9c671',
    alt: 'Tesla EV Charger Installation by Quantum Electrical Services Sleaford',
    label: 'EV Charger Installation',
    category: 'EV Chargers',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/768ce801-92ef-4295-91bc-91337e52d47d_Quantum-Electrical-Services-Sleaford---Consumer-Unit-Upgrade.jpg?v=fc2730dcac9bcfdbedb280779424b26b',
    alt: 'Consumer Unit Upgrade by Quantum Electrical Services Sleaford',
    label: 'Consumer Unit Upgrade',
    category: 'Upgrades',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/146b2ce1-f879-42f3-b61e-3406b406df2e_Solar-Panel-Installation-in-Sleaford-by-Quantum-Electrical-Services02.jpeg?v=a73be763a43c6bdec85f0a8ccb45e430',
    alt: 'Solar Panel Installation in Sleaford by Quantum Electrical Services',
    label: 'Solar Installation',
    category: 'Solar',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/1015436f-88fd-4b86-9ff3-e5cf601d97c7_WhatsApp-Image-2024-07-15-at-20.35.33-1.jpeg?v=152047022aaf1df65e06446757116b09',
    alt: 'Electrical installation work by Quantum Electrical Services',
    label: 'Electrical Installation',
    category: 'Installations',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/2a8f07b0-e688-4691-b4c8-3cf3dff19c1a_WhatsApp-Image-2024-07-15-at-20.35.32.jpeg?v=6eb9dbad91db3baa2d43deae3174642c',
    alt: 'Professional electrical work completed in Lincolnshire',
    label: 'Wiring & Circuits',
    category: 'Installations',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/a2dea59d-8479-4b80-9d82-3f44ea0fd246_WhatsApp-Image-2024-07-15-at-20.35.33.jpeg?v=4d7155cd8db3735b23251d002696e335',
    alt: 'Quality electrical work by Quantum Electrical Sleaford',
    label: 'Electrical Work',
    category: 'Installations',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/22f350dc-f784-4e6d-a116-dfbad1f9391d_WhatsApp-Image-2024-07-15-at-20.35.37-3.jpeg?v=3619e5983fd688e821dd228d5db22979',
    alt: 'Certified electrical installation Sleaford Lincolnshire',
    label: 'Certified Install',
    category: 'Installations',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/11a69976-9566-4b83-9a69-a93eee5056c5_unnamed.jpg?v=36c8eeaa415e16c187349526f8f1bd2a',
    alt: 'Electrical services completed by Quantum Electrical Services',
    label: 'Electrical Services',
    category: 'Installations',
  },
];

const CATEGORIES = ['All', 'Installations', 'Upgrades', 'EV Chargers', 'Solar'];

const COMING_SOON_CATEGORIES = [
  { icon: 'ri-lightbulb-flash-fill', label: 'Lighting Projects', count: '12+ photos' },
  { icon: 'ri-home-wifi-fill', label: 'Smart Home', count: '8+ photos' },
  { icon: 'ri-tools-fill', label: 'Rewires', count: '15+ photos' },
  { icon: 'ri-plug-2-fill', label: 'Socket & Switch', count: '20+ photos' },
  { icon: 'ri-alarm-warning-fill', label: 'Security Lighting', count: '10+ photos' },
  { icon: 'ri-file-certificate-fill', label: 'EICR & Compliance', count: '6+ photos' },
];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightbox, setLightbox] = useState<null | (typeof REAL_PHOTOS)[0]>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? '';

  const jsonLd = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: 'Quantum Electrical Services — Project Gallery',
      description: 'A growing collection of completed electrical projects across Sleaford and Lincolnshire — EV charger installations, consumer unit upgrades, solar panels, and more.',
      url: `${siteUrl}/gallery`,
      author: {
        '@type': 'ElectricalContractor',
        name: 'Quantum Electrical Services',
        url: siteUrl || '/',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Gallery', item: `${siteUrl}/gallery` },
      ],
    },
  ], [siteUrl]);

  const filtered = activeCategory === 'All'
    ? REAL_PHOTOS
    : REAL_PHOTOS.filter((p) => p.category === activeCategory);

  const openLightbox = (photo: (typeof REAL_PHOTOS)[0]) => {
    const idx = filtered.indexOf(photo);
    setLightboxIndex(idx);
    setLightbox(photo);
  };

  const prevPhoto = () => {
    const newIdx = (lightboxIndex - 1 + filtered.length) % filtered.length;
    setLightboxIndex(newIdx);
    setLightbox(filtered[newIdx]);
  };

  const nextPhoto = () => {
    const newIdx = (lightboxIndex + 1) % filtered.length;
    setLightboxIndex(newIdx);
    setLightbox(filtered[newIdx]);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!lightbox) return;
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'ArrowRight') nextPhoto();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  return (
    <>
      <SEO
        title="Gallery | Quantum Electrical Services Sleaford — Our Work"
        description="Browse completed electrical projects by Quantum Electrical Services in Sleaford, Lincolnshire — EV charger installs, consumer unit upgrades, solar panels, rewires, lighting and more."
        keywords="electrical gallery Sleaford, electrician photos Lincolnshire, EV charger installation photos, consumer unit upgrade Sleaford, electrical work photos"
        canonicalPath="/gallery"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #07101F 0%, #0D1E35 60%, #07101F 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C8A96A 0%, transparent 50%), radial-gradient(circle at 80% 50%, #A8894F 0%, transparent 50%)' }} />
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <span className="inline-flex items-center gap-2 bg-[#C8A96A]/10 border border-[#C8A96A]/25 text-[#C8A96A] text-xs tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              <i className="ri-image-2-fill text-xs" />
              Our Work
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Work That Speaks{' '}
              <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">For Itself</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-8">
              A growing collection of completed electrical projects across Sleaford and Lincolnshire — every job certified, documented and built to last.
            </p>
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-xs text-white/40">
              <Link to="/" className="hover:text-[#C8A96A] transition-colors duration-200">Home</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-[#C8A96A]">Gallery</span>
            </nav>
          </div>
        </section>

        {/* Stats strip */}
        <div className="bg-gradient-to-r from-[#0D1E35] to-[#07101F] border-y border-[#C8A96A]/10">
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { val: '500+', label: 'Jobs Completed' },
              { val: '8', label: 'Photos Live Now' },
              { val: '70+', label: 'More Coming Soon' },
              { val: '5★', label: 'Average Rating' },
            ].map(({ val, label }) => (
              <div key={label}>
                <p className="text-[#D4AF37] font-bold text-2xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>{val}</p>
                <p className="text-white/50 text-xs tracking-wide uppercase mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Gallery Section */}
        <section className="py-20 bg-[#0D1E35]">
          <div className="max-w-7xl mx-auto px-6">

            {/* Category filter */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? 'bg-gradient-to-r from-[#C8A96A] to-[#A8894F] text-[#07101F] font-bold'
                      : 'border border-[#C8A96A]/25 text-white/60 hover:text-[#C8A96A] hover:border-[#C8A96A]/50'
                  }`}
                >
                  {cat}
                  {cat === 'All' && <span className="ml-2 text-xs opacity-70">({REAL_PHOTOS.length})</span>}
                </button>
              ))}
            </div>

            {/* Photo grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {filtered.map((photo, i) => (
                  <GalleryCard key={i} photo={photo} index={i} onClick={() => openLightbox(photo)} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-[#C8A96A]/10 border border-[#C8A96A]/20 rounded-full mx-auto mb-4">
                  <i className="ri-image-2-line text-[#C8A96A] text-2xl" />
                </div>
                <p className="text-white/50 text-sm">No photos in this category yet — check back soon!</p>
              </div>
            )}

            <p className="text-center text-white/30 text-xs mt-4">Click any photo to view full size · Use arrow keys to navigate</p>
          </div>
        </section>

        {/* Coming Soon Section */}
        <section className="py-20 bg-[#071120]">
          <div className="max-w-7xl mx-auto px-6">
            <ComingSoonSection />
          </div>
        </section>

        {/* CTA */}
        <CtaSection />
      </main>

      <Footer />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/96 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          {/* Close */}
          <button
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center text-white/60 hover:text-[#C8A96A] text-2xl cursor-pointer transition-colors rounded-full border border-white/10 hover:border-[#C8A96A]/40"
            onClick={() => setLightbox(null)}
          >
            <i className="ri-close-line" />
          </button>

          {/* Prev */}
          {filtered.length > 1 && (
            <button
              className="absolute left-4 md:left-8 w-11 h-11 flex items-center justify-center text-white/60 hover:text-[#C8A96A] text-xl cursor-pointer transition-colors rounded-full border border-white/10 hover:border-[#C8A96A]/40"
              onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            >
              <i className="ri-arrow-left-s-line" />
            </button>
          )}

          {/* Image */}
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full max-h-[82vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          {filtered.length > 1 && (
            <button
              className="absolute right-4 md:right-8 w-11 h-11 flex items-center justify-center text-white/60 hover:text-[#C8A96A] text-xl cursor-pointer transition-colors rounded-full border border-white/10 hover:border-[#C8A96A]/40"
              onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          )}

          {/* Caption */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3">
            <span className="text-white/70 text-sm bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
              {lightbox.label}
            </span>
            <span className="text-white/40 text-xs bg-white/5 px-3 py-2 rounded-full border border-white/10">
              {lightboxIndex + 1} / {filtered.length}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

function GalleryCard({ photo, index, onClick }: { photo: (typeof REAL_PHOTOS)[0]; index: number; onClick: () => void }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-700 ${
        isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
      }`}
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      <img
        src={photo.src}
        alt={photo.alt}
        className="w-full h-64 object-cover object-top group-hover:scale-110 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
          <div>
            <span className="text-white font-semibold text-sm">{photo.label}</span>
            <p className="text-[#C8A96A] text-xs mt-0.5">{photo.category}</p>
          </div>
          <div className="w-9 h-9 flex items-center justify-center bg-[#C8A96A] rounded-full text-black flex-shrink-0">
            <i className="ri-zoom-in-line text-sm" />
          </div>
        </div>
      </div>
      <div className="absolute inset-0 border-2 border-[#C8A96A] rounded-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-300" />
    </div>
  );
}

function ComingSoonSection() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <>
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <span className="inline-flex items-center gap-2 bg-[#C8A96A]/10 border border-[#C8A96A]/25 text-[#C8A96A] text-xs tracking-widest uppercase px-4 py-2 rounded-full mb-6">
          <i className="ri-time-fill text-xs" />
          Coming Soon
        </span>
        <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
          More Photos{' '}
          <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">On the Way</span>
        </h2>
        <p className="text-white/50 max-w-xl mx-auto">
          We&apos;re uploading photos from hundreds of recent jobs across Sleaford and Lincolnshire. Check back soon for the full collection.
        </p>
      </div>

      {/* Coming soon category cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
        {COMING_SOON_CATEGORIES.map((cat, i) => (
          <ComingSoonCard key={i} item={cat} index={i} />
        ))}
      </div>

      {/* Upload CTA box */}
      <div
        className="relative rounded-3xl overflow-hidden border border-[#C8A96A]/20 p-10 text-center"
        style={{ background: 'linear-gradient(135deg, #0D1E35 0%, #091729 100%)' }}
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C8A96A]/40 to-transparent" />
        <div className="w-16 h-16 flex items-center justify-center bg-[#C8A96A]/15 border border-[#C8A96A]/20 rounded-2xl mx-auto mb-5">
          <i className="ri-camera-fill text-[#C8A96A] text-2xl" />
        </div>
        <h3 className="font-playfair text-2xl font-bold text-white mb-3">70+ More Photos Coming</h3>
        <p className="text-white/50 text-sm max-w-md mx-auto mb-6">
          Rewires, lighting projects, smart home installations, security lighting and much more — all being added regularly.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="tel:01529265060"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-[#07101F] font-bold px-6 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-phone-fill" />
            01529 265060
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 border border-[#C8A96A]/35 text-[#C8A96A] hover:bg-[#C8A96A]/10 font-medium px-6 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-mail-fill" />
            Get a Quote
          </Link>
        </div>
      </div>
    </>
  );
}

function ComingSoonCard({ item, index }: { item: { icon: string; label: string; count: string }; index: number }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`relative group rounded-2xl border border-[#C8A96A]/12 overflow-hidden transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ background: 'linear-gradient(135deg, #0D1E35 0%, #091729 100%)', transitionDelay: `${index * 80}ms` }}
    >
      {/* Placeholder image slots */}
      <div className="grid grid-cols-3 gap-0.5 p-0.5">
        {[0, 1, 2].map((j) => (
          <div
            key={j}
            className="h-20 rounded-sm flex items-center justify-center"
            style={{ background: j === 0 ? 'rgba(200,169,106,0.06)' : 'rgba(200,169,106,0.03)' }}
          >
            {j === 1 && <i className="ri-image-add-line text-[#C8A96A]/15 text-xl" />}
          </div>
        ))}
      </div>

      {/* Info */}
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-[#D4AF37]/12 rounded-xl flex-shrink-0">
            <i className={`${item.icon} text-[#D4AF37] text-base`} />
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">{item.label}</p>
            <p className="text-[#C8A96A]/60 text-xs mt-0.5">{item.count}</p>
          </div>
        </div>
        <span className="text-[9px] font-bold tracking-widest uppercase text-[#D4AF37]/50 border border-[#D4AF37]/20 px-2 py-1 rounded-full whitespace-nowrap">
          Soon
        </span>
      </div>
    </div>
  );
}

function CtaSection() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #07101F 0%, #0D1E35 100%)' }}
    >
      <div className="absolute inset-0 opacity-15 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, #C8A96A 0%, transparent 50%)' }} />
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`max-w-3xl mx-auto px-6 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-4">Ready to Start?</p>
        <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
          Want Work This{' '}
          <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">Good?</span>
        </h2>
        <p className="text-white/55 mb-10 max-w-lg mx-auto">
          Contact Quantum Electrical today for a free, no-obligation quote. NAPIT registered electricians covering Sleaford and all of Lincolnshire.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="tel:01529265060"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-[#07101F] font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill" />
            01529 265060
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 border border-[#C8A96A]/40 text-[#C8A96A] hover:bg-[#C8A96A]/10 font-medium px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-fill" />
            Free Quote
          </Link>
        </div>
      </div>
    </section>
  );
}
